import Papa from 'papaparse';

import type { ImportIssue, ImportResult, Paper } from './types';

export type ImportSourceType = ImportResult['sourceType'];

export interface ParsePaperImportOptions {
  sourceType?: ImportSourceType;
  fileName?: string;
}

type SourceRow = Record<string, string>;

const FIELD_ALIASES = {
  id: ['id', 'paperid', 'paper', 'submissionid', 'submission', 'papernumber', 'paper#', '#'],
  title: ['title', 'papertitle', 'submissiontitle'],
  abstract: ['abstract', 'paperabstract', 'summary'],
  topics: ['topics', 'topic', 'keywords', 'keyword', 'subjectareas'],
  track: ['track', 'submissiontrack', 'category'],
  preference: ['preference', 'pref', 'bid', 'bidding', 'reviewerpreference'],
} as const;

function normalizedHeader(header: string): string {
  return header.trim().toLowerCase().replace(/[\s_.:/\\-]+/g, '').replace(/[()[\]{}]/g, '');
}

function stringifyCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  if (Array.isArray(value)) return value.map(stringifyCell).filter(Boolean).join('; ');
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function toSourceRow(value: unknown): SourceRow | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  return Object.fromEntries(
    Object.entries(value).map(([key, cell]) => [key.trim(), stringifyCell(cell)]),
  );
}

function readField(row: SourceRow, aliases: readonly string[]): string {
  const normalizedAliases = new Set(aliases.map(normalizedHeader));
  for (const [header, value] of Object.entries(row)) {
    if (normalizedAliases.has(normalizedHeader(header))) return value.trim();
  }
  return '';
}

const STRONG_TOPIC_DELIMITER = /[;|\r\n]/;
const STRONG_TOPIC_SPLIT = /\s*(?:;|\||\r?\n)\s*/;
const COMMA_TOPIC_SPLIT = /\s*,\s*/;
const INCOMPLETE_TOPIC_START = /^(and|or|etc\.?)\b/i;
const INCOMPLETE_TOPIC_END = /\be\.g\.?$/i;

function uniqueTopics(topics: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of topics) {
    const topic = raw.trim();
    const key = topic.toLocaleLowerCase();
    if (!topic || seen.has(key)) continue;
    seen.add(key);
    result.push(topic);
  }
  return result;
}

/**
 * True when a comma-split fragment looks like its own topic label rather than
 * a leftover from a phrase such as "Web, Mobile, and Emerging Technologies"
 * or "(e.g., abuse, harassment, etc.)".
 */
function looksLikeIndependentTopic(topic: string): boolean {
  if (!topic || INCOMPLETE_TOPIC_START.test(topic) || INCOMPLETE_TOPIC_END.test(topic)) {
    return false;
  }
  if (/^[a-z]/.test(topic)) return false;
  return (topic.match(/\(/g) ?? []).length === (topic.match(/\)/g) ?? []).length;
}

function splitCommaSeparatedTopics(value: string): string[] {
  const parts = value.split(COMMA_TOPIC_SPLIT).map((part) => part.trim()).filter(Boolean);
  if (parts.length <= 1 || parts.every(looksLikeIndependentTopic)) return parts;
  const trimmed = value.trim();
  return trimmed ? [trimmed] : [];
}

function splitTopics(value: string): string[] {
  // HotCRP-style exports use semicolons (and sometimes newlines or pipes)
  // between topics. Commas can legitimately occur inside a topic label,
  // including when that label is a paper's only topic.
  const parts = STRONG_TOPIC_DELIMITER.test(value)
    ? value.split(STRONG_TOPIC_SPLIT)
    : splitCommaSeparatedTopics(value);
  return uniqueTopics(parts);
}

function inferSourceType(source: string, fileName?: string): ImportSourceType {
  const extension = fileName?.toLowerCase().match(/\.([^.]+)$/)?.[1];
  if (extension === 'json') return 'json';
  if (extension === 'csv') return 'csv';
  const first = source.trimStart()[0];
  return first === '[' || first === '{' ? 'json' : 'csv';
}

function looksLikePdf(source: string, fileName?: string): boolean {
  if (fileName?.toLowerCase().endsWith('.pdf')) return true;
  return source.trimStart().startsWith('%PDF');
}

const PDF_IMPORT_MESSAGE =
  'Papers to review are titles and abstracts only. Import a CSV or JSON bidding list—not PDFs. Use Step 1 to extract your own publications.';

function parseCsv(source: string): { rows: SourceRow[]; issues: ImportIssue[] } {
  const parsed = Papa.parse<SourceRow>(source, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: (header) => header.trim(),
    transform: (value) => value.trim(),
  });

  const issues = parsed.errors.map((error) => ({
    ...(typeof error.row === 'number' ? { row: error.row + 2 } : {}),
    message: `CSV parse error: ${error.message}`,
  }));
  return { rows: parsed.data, issues };
}

function parseJson(source: string): { rows: SourceRow[]; issues: ImportIssue[] } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(source);
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'invalid JSON';
    return { rows: [], issues: [{ message: `JSON parse error: ${detail}` }] };
  }

  let candidates: unknown;
  if (Array.isArray(parsed)) {
    candidates = parsed;
  } else if (typeof parsed === 'object' && parsed !== null) {
    const object = parsed as Record<string, unknown>;
    candidates = object.papers ?? object.submissions ?? object.data;
  }

  if (!Array.isArray(candidates)) {
    return {
      rows: [],
      issues: [{ message: 'JSON must be an array, or an object containing papers, submissions, or data.' }],
    };
  }

  const rows: SourceRow[] = [];
  const issues: ImportIssue[] = [];
  candidates.forEach((candidate, index) => {
    const row = toSourceRow(candidate);
    if (row) rows.push(row);
    else issues.push({ row: index + 1, message: 'Expected an object; row was skipped.' });
  });
  return { rows, issues };
}

/**
 * Parse a HotCRP-style CSV export or a JSON paper array. Invalid rows are
 * reported rather than aborting the entire import.
 */
export function parsePaperImport(
  source: string,
  options: ParsePaperImportOptions = {},
): ImportResult {
  const sourceType = options.sourceType ?? inferSourceType(source, options.fileName);
  if (looksLikePdf(source, options.fileName)) {
    return {
      papers: [],
      issues: [{ message: PDF_IMPORT_MESSAGE }],
      sourceType,
    };
  }
  const parsed = sourceType === 'csv' ? parseCsv(source) : parseJson(source);
  const papers: Paper[] = [];
  const issues = [...parsed.issues];
  const seenIds = new Map<string, number>();

  parsed.rows.forEach((sourceRow, index) => {
    const row = index + (sourceType === 'csv' ? 2 : 1);
    const id = readField(sourceRow, FIELD_ALIASES.id);
    const title = readField(sourceRow, FIELD_ALIASES.title);

    if (!id || !title) {
      const missing = [!id && 'paper ID', !title && 'title'].filter(Boolean).join(' and ');
      issues.push({ row, message: `Missing required ${missing}; row was skipped.` });
      return;
    }

    const duplicateRow = seenIds.get(id);
    if (duplicateRow !== undefined) {
      issues.push({
        row,
        message: `Duplicate paper ID "${id}" (first seen on row ${duplicateRow}); row was skipped.`,
      });
      return;
    }
    seenIds.set(id, row);

    papers.push({
      id,
      title,
      abstract: readField(sourceRow, FIELD_ALIASES.abstract),
      topics: splitTopics(readField(sourceRow, FIELD_ALIASES.topics)),
      track: readField(sourceRow, FIELD_ALIASES.track),
      preference: readField(sourceRow, FIELD_ALIASES.preference),
      sourceRow: { ...sourceRow },
    });
  });

  return { papers, issues, sourceType };
}

export const parseImport = parsePaperImport;
